import React from 'react';
import { Form, Input, InputNumber, Checkbox, Card, Space, Button } from 'antd';
import { toast } from 'react-toastify';

import { ApiError, DeviceType, DeviceTypeSettings } from '@services/types';
import { DeviceTypeSettingFormItem, formConfig } from './constants';
import api from '@services/api';

import styles from './device-type-settings-modal.module.scss';
import { useHistory, useParams } from 'react-router-dom';
import { Navigation } from '@constants/Navigation';

const InputTooltip = ({ item }: { item: DeviceTypeSettingFormItem }) => {
  return (
    <div>
      <strong>{item.description.header}</strong>
      <ul>
        {item.description.rules.map((rule) => (
          <li key={rule}>{rule}</li>
        ))}
      </ul>
    </div>
  );
};

const DeviceTypeSettingsPage: React.FC = () => {
  const { id: deviceTypeId }: { id: string } = useParams();
  const history = useHistory();
  const [form] = Form.useForm<DeviceTypeSettings>();
  const [deviceType, setDeviceType] = React.useState<DeviceType>();

  React.useEffect(() => {
    api.administration.getDeviceTypeSettings(deviceTypeId).then(form.setFieldsValue);
    api.administration.getDeviceType(deviceTypeId).then(setDeviceType);
  }, [form, deviceTypeId]);

  const handleClose = () => {
    history.push(Navigation.DeviceType);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      await api.administration.updateDeviceTypeSettings(deviceTypeId, values);
      toast.success('Device Type settings was updated');
      form.resetFields();
      handleClose();
    } catch (error) {
      if (error instanceof ApiError) {
        toast.error(`${error.error.details || Object.values(error.error.propertyErrors!).map((item) => item)}`);
      } else {
        toast.error('Device Type settings was not updated');
      }
      console.log('DeviceTypeSettingsPage ~ handleSubmit ~ error', error);
    }
  };

  return (
    <Card
      title={`Edit Device Type Settings: ${deviceType?.deviceName || ''}`}
      extra={
        <Space>
          <Button type="default" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="primary" onClick={handleSubmit}>
            Save
          </Button>
        </Space>
      }
    >
      <Form form={form} className={styles.form} layout="horizontal" name="form_in_modal">
        {formConfig.string.map((item) => (
          <Form.Item
            key={item.field}
            name={item.field}
            initialValue={item.value?.default || ''}
            label={item.label}
            tooltip={<InputTooltip item={item} />}
            style={{ gridArea: item.field }}
          >
            <Input minLength={item.value?.min || 0} maxLength={item.value?.max || 256} />
          </Form.Item>
        ))}
        {formConfig.number.map((item) => (
          <Form.Item
            key={item.field}
            initialValue={item.value.default}
            name={item.field}
            tooltip={<InputTooltip item={item} />}
            label={item.label}
            style={{ gridArea: item.field }}
          >
            <InputNumber min={item.value.min} max={item.value.max} />
          </Form.Item>
        ))}
        {formConfig.bool.map((item) => (
          <Form.Item
            key={item.field}
            name={item.field}
            tooltip={<InputTooltip item={item} />}
            initialValue={item.value?.default}
            label={item.label}
            style={{ gridArea: item.field }}
            valuePropName="checked"
          >
            <Checkbox />
          </Form.Item>
        ))}
      </Form>
    </Card>
  );
};

export default React.memo(DeviceTypeSettingsPage);
